import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Stack } from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React from 'react';

import ClickAwayListener from '@/components/ui/ClickAwayListener/ClickAwayListener';
import { useBasketContext } from '@/hooks/useBasketContext';
import { useBasketPreviewContext } from '@/hooks/useBasketPreviewContext';
import useFormatMessage from '@/hooks/useFormatMessage';
import { useMobileMediaQuery } from '@/hooks/useMobileMediaQuery';

import { Badge, BasketText, Wrapper } from './HeaderBasket.style';
import BasketPreview from './lib/BasketPreview';

const HeaderBasket = ({ 'data-test-subject': dataTestSubject, className }) => {
  const { requestsInBasketCount } = useBasketContext();
  const {
    openBasketPreview,
    closeBasketPreview,
    basketPreviewOpen: open,
    setBasketPreviewHovered,
  } = useBasketPreviewContext();
  const mobileMediaQuery = useMobileMediaQuery();
  const { pathname } = useRouter();
  const formatMessage = useFormatMessage();

  const pathWithoutBasketPreview = ['/basket', '/order-details'].some(el =>
    pathname.includes(el)
  );

  const handleClose = e => {
    // prevent preview to be closed on blur - after interacting with elements inside preview
    if (open && e.type !== 'blur') {
      closeBasketPreview();
    }
  };

  const basketIcon = (
    <Badge badgeContent={requestsInBasketCount} color="secondary">
      <ShoppingCartIcon fontSize="small" color="primary" />
    </Badge>
  );

  if (pathWithoutBasketPreview) {
    return (
      <Wrapper
        data-test-subject={dataTestSubject}
        className={className}
        style={{ cursor: 'auto' }}>
        {basketIcon}
      </Wrapper>
    );
  }

  if (mobileMediaQuery) {
    return (
      <Link href="/basket">
        <Wrapper data-test-subject={dataTestSubject} className={className}>
          {basketIcon}
        </Wrapper>
      </Link>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <BasketPreview
          open={open}
          onClose={handleClose}
          // written as function not to pass event parameter from onOpen
          onOpen={() => openBasketPreview()}
          basketEmpty={requestsInBasketCount === 0}
          onMouseEnter={() => setBasketPreviewHovered(true)}
          onMouseLeave={() => setBasketPreviewHovered(false)}>
          <Wrapper data-test-subject={dataTestSubject} className={className}>
            {basketIcon}
          </Wrapper>
        </BasketPreview>
        {!mobileMediaQuery && (
          <BasketText
            component="div"
            color="text.secondary"
            onMouseEnter={() => openBasketPreview()}>
            {formatMessage('header_basketLabel')}
          </BasketText>
        )}
      </Stack>
    </ClickAwayListener>
  );
};

HeaderBasket.propTypes = {
  className: PropTypes.string,
  'data-test-subject': PropTypes.string,
};

HeaderBasket.defaultProps = {
  className: '',
  'data-test-subject': '',
};

export default HeaderBasket;
